import type { FormInstance } from "antd";
import { useIntl } from "react-intl";
import { FormHelpSection } from "~components/Form/FormHelpSection";
import { FormInput } from "~components/Form/FormInput";
import type { UpsertPersonForm } from "~pages/Persons/PersonProfile/types/upsertPersonForm.ts";
import { cmToFeetInches, feetInchesToCm, kgToLbs, lbsToKg } from "~pages/Persons/PersonProfile/utils/utils.ts";

const fields = [
  { titleIntlKey: "height", descriptionIntlKey: "form.help.person.height" },
  {
    titleIntlKey: "heightImperial",
    descriptionIntlKey: "form.help.person.heightImperial",
  },
  { titleIntlKey: "weight", descriptionIntlKey: "form.help.person.weight" },
  {
    titleIntlKey: "weightImperial",
    descriptionIntlKey: "form.help.person.weightImperial",
  },
  {
    titleIntlKey: "representation",
    descriptionIntlKey: "form.help.person.representing",
  },
  {
    titleIntlKey: "juniorAssociationLeague",
    descriptionIntlKey: "form.help.person.juniorAssociationLeague",
  },
  { titleIntlKey: "homeTown", descriptionIntlKey: "form.help.person.homeTown" },
  {
    titleIntlKey: "college",
    descriptionIntlKey: "form.help.person.collegeClass",
  },
  {
    titleIntlKey: "collegeClass",
    descriptionIntlKey: "form.help.person.college",
  },
  { titleIntlKey: "school", descriptionIntlKey: "form.help.person.school" },
  {
    titleIntlKey: "schoolClass",
    descriptionIntlKey: "form.help.person.schoolClass",
  },
];

type Props = {
  isLoading: boolean;
  form: FormInstance<UpsertPersonForm>;
};

export const PersonalTab = ({ isLoading, form }: Props) => {
  const { formatMessage } = useIntl();

  const handleWeightChange = (value: number | null) => {
    if (value === null) {
      form.setFieldValue(["additionalDetails", "weightImperial"], null);
      return;
    }

    const weightInLbs = kgToLbs(value);
    form.setFieldValue(["additionalDetails", "weightImperial"], weightInLbs);
  };

  const handleWeightImperialChange = (value: number | null) => {
    if (value === null) {
      form.setFieldValue(["additionalDetails", "weight"], null);
      return;
    }

    const weightInKg = lbsToKg(value);
    form.setFieldValue(["additionalDetails", "weight"], weightInKg);
  };

  const handleHeightChange = (value: number | null) => {
    if (value === null) {
      form.setFieldValue(["additionalDetails", "heightImperial"], null);
      return;
    }

    const heightImperial = cmToFeetInches(value);
    form.setFieldValue(["additionalDetails", "heightImperial"], heightImperial);
  };

  const handleHeightImperialChange = (value: string | null) => {
    if (!value) {
      form.setFieldValue(["additionalDetails", "height"], null);
      return;
    }

    const heightInCm = feetInchesToCm(value);
    if (heightInCm !== null) {
      form.setFieldValue(["additionalDetails", "height"], heightInCm);
    }
  };

  return (
    <div className="person-profile-columns">
      <FormHelpSection
        modalTitle={formatMessage({ id: "person.additional.information" })}
        modalSubTitle={formatMessage({ id: "details" })}
        fields={fields}
      />
      <div className="form-additional-details-grid">
        <FormInput<UpsertPersonForm>
          label={formatMessage({ id: "height" })}
          showSkeleton={isLoading}
          name={["additionalDetails", "height"]}
          normalize={(value) => (value ? Number(value) : null)}
          inputProps={{
            placeholder: formatMessage({ id: "height" }),
            type: "number",
            min: 122,
            max: 241,
            onChange: (e) => handleHeightChange(e.target.value ? Number(e.target.value) : null),
          }}
          rules={[
            {
              type: "number",
              min: 122,
              message: formatMessage({
                id: "form.help.person.height.min",
              }),
            },
            {
              type: "number",
              max: 241,
              message: formatMessage({
                id: "form.help.person.height.max",
              }),
            },
          ]}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "heightImperial" })}
          name={["additionalDetails", "heightImperial"]}
          inputProps={{
            placeholder: formatMessage({ id: "heightImperial" }),
            onChange: (e) => handleHeightImperialChange(e.target.value || null),
          }}
          rules={[
            {
              validator: async (_, value) => {
                if (!value) return;

                const match = value.match(/^(\d)'(\d{1,2})$/);
                if (!match) return;

                const feet = Number.parseInt(match[1], 10);
                const inches = Number.parseInt(match[2], 10);
                const totalInches = feet * 12 + inches;

                if (totalInches < 48) {
                  throw new Error(
                    formatMessage({
                      id: "form.help.person.heightImperial.min",
                    }),
                  );
                }
                if (totalInches > 95) {
                  throw new Error(
                    formatMessage({
                      id: "form.help.person.heightImperial.max",
                    }),
                  );
                }
              },
            },
            {
              pattern: /^([4-7])'([0-9]|1[0-1])$/,
              message: formatMessage({
                id: "validation.invalid.heightImperial",
              }),
            },
          ]}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "weight" })}
          name={["additionalDetails", "weight"]}
          inputProps={{
            placeholder: formatMessage({ id: "weight" }),
            type: "number",
            min: 30,
            max: 180,
            onChange: (e) => handleWeightChange(e.target.value ? Number(e.target.value) : null),
          }}
          normalize={(value) => (value ? Number(value) : null)}
          rules={[
            {
              type: "number",
              min: 30,
              message: formatMessage({ id: "form.help.person.weight.min" }),
            },
            {
              type: "number",
              max: 180,
              message: formatMessage({ id: "form.help.person.weight.max" }),
            },
          ]}
        />

        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "weightImperial" })}
          name={["additionalDetails", "weightImperial"]}
          inputProps={{
            placeholder: formatMessage({ id: "weightImperial" }),
            type: "number",
            min: 66,
            max: 397,
            onChange: (e) => handleWeightImperialChange(e.target.value ? Number(e.target.value) : null),
          }}
          normalize={(value) => (value ? Number(value) : null)}
          rules={[
            {
              type: "number",
              min: 66,
              message: formatMessage({
                id: "form.help.person.weightImperial.min",
              }),
            },
            {
              type: "number",
              max: 397,
              message: formatMessage({
                id: "form.help.person.weightImperial.max",
              }),
            },
          ]}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "representation" })}
          name={["additionalDetails", "representation"]}
          inputProps={{ placeholder: formatMessage({ id: "representation" }) }}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "juniorAssociationLeague" })}
          name={["additionalDetails", "juniorAssociationLeague"]}
          inputProps={{
            placeholder: formatMessage({ id: "juniorAssociationLeague" }),
          }}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "homeTown" })}
          name={["additionalDetails", "homeTown"]}
          inputProps={{ placeholder: formatMessage({ id: "homeTown" }) }}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "college" })}
          name={["additionalDetails", "college"]}
          inputProps={{ placeholder: formatMessage({ id: "college" }) }}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "collegeClass" })}
          name={["additionalDetails", "collegeClass"]}
          inputProps={{ placeholder: formatMessage({ id: "collegeClass" }) }}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "school" })}
          name={["additionalDetails", "school"]}
          inputProps={{ placeholder: formatMessage({ id: "school" }) }}
        />
        <FormInput<UpsertPersonForm>
          showSkeleton={isLoading}
          label={formatMessage({ id: "schoolClass" })}
          name={["additionalDetails", "schoolClass"]}
          inputProps={{ placeholder: formatMessage({ id: "schoolClass" }) }}
        />
      </div>
    </div>
  );
};
